import type {
  Portfolio,
  Position,
} from '@blk/aladdin-graph-client-axios-internal/wealth/legacy/aw_portfolio/v1/portfolio_api';
import { detectEnvironment, isCloud } from '@blk/aw-env-detection';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { cloneDeep, get, set, unset } from 'lodash';
import { minimatch } from 'minimatch';
import moment from 'moment';
import { awSensitiveDataSettings } from '../api/SensitiveData';
import {
  ACCEPT_HEADER,
  AGraphAcceptType,
  AGraphEnvType,
  API_KEY_HEADER,
  CHANNEL_HEADER,
  EnvType,
  ORIGIN_TIME_STAMP_HEADER,
  REQUEST_ID_HEADER,
  STRIPE_HEADER,
  TRACEPARENT_HEADER,
} from '../APIConstants';
import { getPkceConfig } from '../config/pkce';
import { AUTHORIZATION_HEADER_ID, DEFAULT_BEARER_AUTH_SCHEME, setHeaderRequestId } from './HeaderUtils';

/**
 * Default maximum recursion depth for transform functions.
 * Can be overridden via:
 * 1. Environment variable: AW_API_MAX_RECURSION_DEPTH
 * 2. Per-API configuration: ResponseTransformConfig.maxDepth
 * 3. Per-call options: RecursiveTransformOptions.maxDepth
 *
 * Precedence: Per-call options > Per-API config > Environment variable > This default (3)
 *
 * Depth of 3 is based on actual Portfolio/Aggregate nesting behavior:
 * - Portfolio → Model → Benchmark → BenchmarkRef (3 levels)
 * - Benchmarks render 1 level + reference
 * - Models render up to 2 nested levels + reference
 *
 * @default 3
 * @example
 * ```typescript
 * // Set via environment variable:
 * // export AW_API_MAX_RECURSION_DEPTH=3
 *
 * // Use in config (omit maxDepth to use this default):
 * // { urlPattern: '/v1/portfolios/*', timestampFields: [...] }
 * ```
 */
export const DEFAULT_MAX_RECURSION_DEPTH = parseInt(process.env.AW_API_MAX_RECURSION_DEPTH || '3', 10);

function getApiEnvironment(envType: EnvType | undefined, mapNonProdToBeta: boolean = false) {
  if (envType === EnvType.BETA || envType === EnvType.UAT || envType === EnvType.QA) {
    if (mapNonProdToBeta) {
      return EnvType.BETA;
    }
    return envType ?? '';
  }
  return '';
}

export function getApiBaseUrlSuffix(
  envType: EnvType | undefined,
  envSeparator: string,
  mapNonProdToBeta: boolean = false
) {
  const env = getApiEnvironment(envType, mapNonProdToBeta);
  return env ? envSeparator + env : '';
}

export function insertApiEnvironmentIntoBaseUrl(
  baseUrl: string,
  insertBefore: string,
  envType: EnvType | undefined,
  envSeparator: string,
  mapNonProdToBeta: boolean = false
) {
  const env = getApiEnvironment(envType, mapNonProdToBeta);
  const insertIndex = baseUrl.indexOf(insertBefore);
  if (!env || insertIndex === -1) {
    return baseUrl;
  }

  return baseUrl.slice(0, insertIndex) + envSeparator + env + baseUrl.slice(insertIndex);
}

// Generate a unique trace parent ID
export const generateTraceParent = () => {
  const version = '00';
  const traceId1 = [...Array(32)].map(() => Math.floor(Math.random() * 16).toString(16)).join('');
  const spanId = [...Array(16)].map(() => Math.floor(Math.random() * 16).toString(16)).join('');
  const traceFlags = '01';
  return `${version}-${traceId1}-${spanId}-${traceFlags}`;
};

/**
 * Set all the headers required for AGraph APIs
 */
export function setAGraphHeaders(
  config: AxiosRequestConfig,
  apiKeyHeader?: string,
  envType?: EnvType,
  envMode: 'stripe' | 'channel' = 'stripe',
  mapNonProdToBeta: boolean = true
) {
  setHeaderRequestId(config);
  config.headers = config.headers || {};

  const originTimeStamp = moment().toISOString();
  config.headers[ORIGIN_TIME_STAMP_HEADER] = originTimeStamp;

  if (envType) {
    if (envMode === 'channel') {
      const channel =
        getApiEnvironment(envType) === EnvType.BETA || getApiEnvironment(envType) === EnvType.UAT
          ? AGraphEnvType.BETA
          : undefined;

      config.headers[CHANNEL_HEADER] = channel;
    } else {
      let stripe;
      if (mapNonProdToBeta && getApiEnvironment(envType, mapNonProdToBeta) === EnvType.BETA) {
        stripe = AGraphEnvType.BETA;
      } else {
        /*
         * This is not a typo! When using beta AND uat servers, we have to map the stripes like this:
         * alpha stripe -> beta instance
         * beta stripe -> uat instance
         */
        if (getApiEnvironment(envType) === EnvType.BETA) {
          stripe = AGraphEnvType.ALPHA;
        }
        if (getApiEnvironment(envType) === EnvType.UAT) {
          stripe = AGraphEnvType.BETA;
        }
      }

      // Unlike channels, we only need to set a 'stripe' header if it's not the default
      if (stripe) {
        config.headers[STRIPE_HEADER] = stripe;
      }
    }
  }
  // Retrieve PKCE access token from session storage
  const { getIsJwtTokenRequired, getJwtTokenRef } = awSensitiveDataSettings;
  /*
   * Use the JWT token if PKCE is required and the token retrieval function is available
   * Otherwise, fall back to using the API key header if provided
   */
  if (getIsJwtTokenRequired() && getJwtTokenRef?.current && config.headers) {
    // Set the Authorization header with the PKCE access token
    config.headers[AUTHORIZATION_HEADER_ID] = `${DEFAULT_BEARER_AUTH_SCHEME} ${getJwtTokenRef.current()}`;
  }

  if (apiKeyHeader && !isCloud()) {
    config.headers[API_KEY_HEADER] = apiKeyHeader;
  }

  // Finally, if we're on external AGraph, we need to fix a bunch of headers... for AGraph reasons
  if (isCloud()) {
    // First add a traceparent header
    config.headers[TRACEPARENT_HEADER] = generateTraceParent();

    /*
     * Channel or Stripe => Accept
     * Temporarily removing the channel => accept transform as it doesn't seem to work
     * This means that, for now, if the API uses channels, we are always pointing to the "stable" version over the internet until the AGraph team fixes it
     * if (config.headers[CHANNEL_HEADER] === AGraphEnvType.BETA || config.headers[STRIPE_HEADER] === AGraphEnvType.BETA) {
     */
    if (config.headers[STRIPE_HEADER] === AGraphEnvType.BETA) {
      config.headers[ACCEPT_HEADER] = AGraphAcceptType.BETA;
    }
    if (config.headers[STRIPE_HEADER] === AGraphEnvType.ALPHA) {
      config.headers[ACCEPT_HEADER] = AGraphAcceptType.ALPHA;
    }

    // Clear out the old headers
    config.headers[API_KEY_HEADER] = undefined;
    config.headers[CHANNEL_HEADER] = undefined;
    config.headers[ORIGIN_TIME_STAMP_HEADER] = undefined;
    config.headers[REQUEST_ID_HEADER] = undefined;
    config.headers[STRIPE_HEADER] = undefined;
  }
}

/*
 * Remove duplicate prefixes from config URLs, specified in an API Base.ts
 * This is so we don't have to change every path in every function when migrating to AGraph
 */
export function modifyAGraphURL(config: AxiosRequestConfig, configURLPrefixes: string[]) {
  configURLPrefixes.forEach((prefix) => {
    if (config.url?.startsWith(prefix)) {
      config.url = config.url.replace(prefix, '');
    }
  });
}

/**
 * Get the AGraph base URL for API calls
 *
 * Constructs the appropriate base URL based on the current environment:
 * - Returns external AGraph URL for recognized environments (dev, tst, uat, prod)
 * - Returns relative URL for unknown environments (graceful fallback)
 *
 * @param baseURL - The relative API endpoint path (e.g., '/api/test')
 * @returns Complete URL with environment-specific base, or original relative URL
 *
 * Note: When detectEnvironment() returns undefined (unknown hostname), this function
 *       returns the relative URL to ensure the app continues working with same-origin APIs
 */
export const getAGraphBaseUrl = (baseURL: string) => {
  const env = detectEnvironment();
  const config = getPkceConfig(env);

  /*
   * For all other environments, always use the external AGraph URL from config if available
   * When config is undefined (unknown environment), falls back to relative URL
   */
  if (config && !!config.apiBaseUrl) {
    return config.apiBaseUrl + baseURL;
  }

  return baseURL;
};

export const getRequestUrlFromConfig = (config: AxiosRequestConfig | undefined): string => axios.getUri(config);

/**
 * Options for configuring recursive transformation behavior
 */
export interface RecursiveTransformOptions {
  /** Maximum recursion depth (default: 3) */
  maxDepth?: number;
  /**
   * Field name patterns that indicate recursive structures to recurse into.
   * Case-insensitive substring matching: 'model' matches 'model', 'modelRef', 'otherModels', etc.
   * Must be explicitly specified - no default to avoid assumptions about data structure.
   * Use empty array [] for flat object transformation (no recursion).
   */
  recursiveFieldPatterns: string[];
  /** Current depth (internal - do not set manually) */
  _currentDepth?: number;
}

/**
 * Type guard to check if a value is a plain object (not null, not array)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- type guard needs any
function isObject(value: any): value is Record<string, any> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Check if a field name matches any of the provided patterns (case-insensitive substring match)
 * @param fieldName - The field name to check
 * @param patterns - Array of patterns to match against
 * @returns true if field name contains any pattern (case-insensitive)
 */
/**
 * Check if a field name exactly matches one of the provided patterns (case-insensitive).
 * Used to determine if a field should be transformed.
 */
function matchesFieldName(fieldName: string, patterns: string[]): boolean {
  const lowerFieldName = fieldName.toLowerCase();
  return patterns.some((pattern) => lowerFieldName === pattern.toLowerCase());
}

/**
 * Check if a field name contains one of the provided patterns as substring (case-insensitive).
 * Used to determine if recursive traversal should continue into nested structures.
 * Special pattern '*' matches all fields (for generic traversal).
 */
function matchesRecursivePattern(fieldName: string, patterns: string[]): boolean {
  // Wildcard pattern '*' matches everything
  if (patterns.includes('*')) {
    return true;
  }
  const lowerFieldName = fieldName.toLowerCase();
  return patterns.some((pattern) => lowerFieldName.includes(pattern.toLowerCase()));
}

/**
 * Check if a field name indicates a recursive structure that should be traversed
 * Uses pattern matching to detect fields like 'model', 'benchmark', 'childAggregates', 'portfolios', etc.
 */
function isRecursiveField(fieldName: string, recursivePatterns: string[]): boolean {
  return matchesRecursivePattern(fieldName, recursivePatterns);
}

/**
 * Recursively transform field values in nested object structures.
 * Automatically handles deeply nested structures like Portfolio (with recursive model/benchmark fields)
 * and AggregateComposition (with childAggregates arrays).
 *
 * This replaces the JMESPath-based approach that requires exhaustive path enumeration.
 * Instead of specifying every possible path (e.g., '*.items[].benchmarkPortfolio.asOf',
 * '*.items[].modelPortfolio.asOf', etc.), you simply provide field names (e.g., ['asOf'])
 * and the function automatically recurses through nested structures.
 *
 * @param data - The data object to transform
 * @param fieldNames - Array of field names to transform (e.g., ['asOf', 'creationTimestamp', 'origin'])
 * @param transformFn - Function to apply to matching field values. Receives (value, fieldName) and returns transformed value
 * @param options - Optional configuration for recursion behavior
 * @returns New object with transformed fields (immutable - original object unchanged)
 *
 * @example
 * ```typescript
 * // Transform enum fields in nested Portfolio structure
 * const portfolio = {
 *   origin: 'PORTFOLIO_ORIGIN_USER',
 *   model: {
 *     origin: 'PORTFOLIO_ORIGIN_SYSTEM',
 *     benchmark: {
 *       origin: 'PORTFOLIO_ORIGIN_DEMO'
 *     }
 *   }
 * };
 *
 * const result = recursiveTransform(
 *   portfolio,
 *   ['origin'],
 *   (value) => value.replace('PORTFOLIO_ORIGIN_', ''),
 *   { maxDepth: 3, recursiveFieldPatterns: ['model', 'benchmark'] }
 * );
 * // Result: All 3 'origin' fields transformed at all nesting levels
 * ```
 *
 * @example
 * ```typescript
 * // Transform timestamp fields with field-specific logic
 * const data = {
 *   asOf: 1234567890,
 *   origin: 'USER',
 *   model: { asOf: 9876543210 }
 * };
 *
 * const result = recursiveTransform(
 *   data,
 *   ['asOf', 'origin'],
 *   (value, fieldName) => {
 *     if (fieldName === 'asOf') return String(value);
 *     if (fieldName === 'origin') return `PREFIX_${value}`;
 *     return value;
 *   },
 *   { recursiveFieldPatterns: ['model'] }
 * );
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic transformation needs any
export function recursiveTransform<T extends Record<string, any>>(
  data: T,
  fieldNames: string[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- transform function handles arbitrary value types
  transformFn: (value: any, fieldName: string) => any,
  options?: RecursiveTransformOptions
): T {
  if (!data) {
    return data;
  }

  // Default options
  const { maxDepth = DEFAULT_MAX_RECURSION_DEPTH, recursiveFieldPatterns, _currentDepth = 0 } = options || {};

  if (!recursiveFieldPatterns) {
    throw new Error('recursiveFieldPatterns must be provided (use empty array [] for flat object transformation)');
  }

  // Check depth limit
  if (_currentDepth >= maxDepth) {
    return data;
  }

  // Clone to avoid mutation
  const clonedData = cloneDeep(data);

  // Traverse and transform
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- recursive traversal requires any
  const traverse = (obj: any, depth: number): any => {
    if (depth > maxDepth) {
      return obj;
    }

    if (Array.isArray(obj)) {
      // Recurse into arrays
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- recursive traversal of arbitrary arrays
      return obj.map((item) => traverse(item, depth));
    }

    if (isObject(obj)) {
      // Process each key in the object
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- building arbitrary result object
      const result: Record<string, any> = {};

      Object.keys(obj).forEach((key) => {
        let value = obj[key];

        /*
         * Check if we should recurse into this field
         * Always recurse into arrays, only recurse into objects if they match recursive patterns
         */
        if (Array.isArray(value) || (isObject(value) && isRecursiveField(key, recursiveFieldPatterns))) {
          value = traverse(value, depth + 1);
        }

        // Apply transformation only to fields matching fieldNames
        if (matchesFieldName(key, fieldNames)) {
          // If value is an array of primitives, map transformFn over each element
          if (Array.isArray(value) && value.length > 0 && !isObject(value[0])) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- transformFn handles arbitrary value types
            value = value.map((item) => transformFn(item, key));
          } else {
            value = transformFn(value, key);
          }
        }

        result[key] = value;
      });

      return result;
    }

    // Primitives - return as-is
    return obj;
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- traverse returns transformed object
  return traverse(clonedData, _currentDepth);
}

/**
 * Transform timestamp fields in both directions (number ↔ string).
 * Uses recursive traversal to handle nested structures automatically.
 *
 * @param data - The data object to transform
 * @param fieldNames - Array of timestamp field names to transform (e.g., ['asOf', 'creationTimestamp'])
 * @param direction - 'stringify' converts numbers to strings, 'normalize' converts strings to numbers
 * @param options - Recursive traversal options (maxDepth, recursiveFieldPatterns)
 * @returns New object with transformed timestamps (immutable)
 *
 * @example
 * ```typescript
 * // Request (stringify):
 * const payload = { asOf: 1234567890, model: { asOf: 9876543210 } };
 * const result = transformTimestamps(payload, ['asOf'], 'stringify',
 *   { maxDepth: 3, recursiveFieldPatterns: ['model', 'benchmark', 'portfolio'] });
 * // Result: { asOf: '1234567890', model: { asOf: '9876543210' } }
 *
 * // Response (normalize):
 * const response = { asOf: '1234567890', model: { asOf: '9876543210' } };
 * const result = transformTimestamps(response, ['asOf'], 'normalize',
 *   { maxDepth: 3, recursiveFieldPatterns: ['model', 'benchmark', 'portfolio'] });
 * // Result: { asOf: 1234567890, model: { asOf: 9876543210 } }
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- any shape could use this
export function transformTimestamps<T extends Record<string, any>>(
  data: T,
  fieldNames: string[],
  direction: 'stringify' | 'normalize',
  options?: RecursiveTransformOptions
): T {
  if (!data) {
    return data;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- handles arbitrary values (numbers, strings, etc.)
  const transformFn = (value: any): any => {
    if (direction === 'stringify') {
      // Convert numbers to strings
      return typeof value === 'number' ? String(value) : value;
    } else {
      // normalize - convert strings to numbers
      if (typeof value === 'string') {
        const parsed = Number(value);
        return isNaN(parsed) ? value : parsed;
      }
      return value;
    }
  };

  return recursiveTransform(data, fieldNames, transformFn, options);
}

/**
 * Prefix an enum value for AGraph compatibility.
 * AGraph APIs often require enum values with specific prefixes (e.g., USER_ATTRIBUTE_TYPE_).
 * This function adds the prefix if not already present.
 *
 * @param value - The enum value to prefix (e.g., 'MONITORING_DASHBOARD_TABLE_ATTRIBUTES')
 * @param prefix - The prefix to add (e.g., 'USER_ATTRIBUTE_TYPE_')
 * @param shouldPrefix - Whether the value should be prefixed
 * @returns The prefixed value (e.g., 'USER_ATTRIBUTE_TYPE_MONITORING_DASHBOARD_TABLE_ATTRIBUTES') if `shouldPrefix` is true,
 * otherwise the original value is returned.
 */
export function prefixEnumForAGraph(
  value: string | undefined,
  prefix: string,
  shouldPrefix = false
): string | undefined {
  if (value === undefined || value === null || typeof value !== 'string') {
    return value;
  }

  if (!shouldPrefix || value.startsWith(prefix)) {
    return value;
  }

  return `${prefix}${value}`;
}

/**
 * Remove a prefix from an enum value for client consumption.
 * This is the reverse operation of prefixEnumForAGraph.
 *
 * @param value - The prefixed enum value (e.g., 'USER_ATTRIBUTE_TYPE_MONITORING_DASHBOARD_TABLE_ATTRIBUTES')
 * @param prefix - The prefix to remove (e.g., 'USER_ATTRIBUTE_TYPE_')
 * @param shouldRemovePrefix - Whether the prefix should be removed
 * @returns The unprefixed value (e.g., 'MONITORING_DASHBOARD_TABLE_ATTRIBUTES') if `shouldRemovePrefix` is true,
 * otherwise the original value is returned.
 */
export function removePrefixFromAGraphEnum(
  value: string | undefined,
  prefix: string,
  shouldRemovePrefix = false
): string | undefined {
  if (value === undefined || value === null || typeof value !== 'string') {
    return value;
  }

  if (!shouldRemovePrefix || !value.startsWith(prefix)) {
    return value;
  }

  return value.slice(prefix.length);
}

/**
 * Configuration for prefixing enum fields in AGraph payloads.
 */
export interface EnumPrefixConfig {
  /** Field names to match (e.g., ['origin', 'valueType']) - automatically handles all nesting levels */
  fieldNames: string[];
  /** The prefix to add/remove (e.g., 'ORIGIN_', 'VALUE_TYPE_') */
  prefix: string;
  /**
   * Patterns to control which nested structures to recurse into (e.g., ['model', 'benchmark', 'filters']).
   * Required to specify where to look for nested enum fields.
   */
  recursiveFieldPatterns: string[];
}

/**
 * Prefix enum values in a data payload for AGraph compatibility.
 * Automatically handles deeply nested structures (Portfolio with model/benchmark, AggregateComposition with childAggregates).
 * Returns a new object with prefixed values (does not mutate the original).
 *
 * @param data - The data payload to modify (arbitrary object structure)
 * @param configs - Array of configurations specifying which fields to prefix and with what prefix
 * @returns A new object with the prefixed enum values
 *
 * @example
 * ```typescript
 * // Before (old config with dozens of expressions):
 * // { expression: '*.items[].origin', prefix: 'ORIGIN_' },
 * // { expression: '*.items[].benchmarkPortfolio.origin', prefix: 'ORIGIN_' },
 * // { expression: '*.items[].modelPortfolio.origin', prefix: 'ORIGIN_' },
 * // ... 20+ more expressions for nested origins
 *
 * // After (new simplified config):
 * const filterList: AWFilterList = { filters: [{ operator: 'EQUAL', valueType: 'VARCHAR' }] };
 * if (enableAGraph) {
 *   const modified = prefixEnumsForAGraph(filterList, [
 *     { fieldNames: ['operator'], prefix: 'FILTER_OPERATOR_', recursiveFieldPatterns: ['filters'] },
 *     { fieldNames: ['valueType'], prefix: 'FILTER_VALUE_TYPE_', recursiveFieldPatterns: ['filters'] }
 *   ]);
 * }
 * // Result: { filters: [{ operator: 'FILTER_OPERATOR_EQUAL', valueType: 'FILTER_VALUE_TYPE_VARCHAR' }] }
 * // Automatically handles all nesting levels!
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- any shape could use this
export function prefixEnumsForAGraph<T extends Record<string, any>>(
  data: T,
  configs: EnumPrefixConfig[],
  options?: Pick<RecursiveTransformOptions, 'maxDepth'>
): T {
  if (!data) {
    return data;
  }

  const { maxDepth = DEFAULT_MAX_RECURSION_DEPTH } = options || {};

  return configs.reduce(
    (result, { fieldNames, prefix, recursiveFieldPatterns }) =>
      recursiveTransform(
        result,
        fieldNames,
        (value) => {
          const prefixed = prefixEnumForAGraph(value as string | undefined, prefix, true);
          return typeof prefixed === 'string' ? prefixed.toUpperCase() : prefixed;
        },
        {
          maxDepth,
          recursiveFieldPatterns,
        }
      ),
    data
  );
}

/**
 * Remove enum prefixes from a response payload for client consumption.
 * This is the reverse operation of prefixEnumsForAGraph.
 * Automatically handles deeply nested structures (Portfolio with model/benchmark, AggregateComposition with childAggregates).
 * Returns a new object with unprefixed values (does not mutate the original).
 *
 * @param data - The response data to modify (arbitrary object structure)
 * @param configs - Array of configurations specifying which fields to unprefix and with what prefix
 * @returns A new object with the unprefixed enum values
 *
 * @example
 * ```typescript
 * // Before (old config with dozens of expressions):
 * // { expression: '*.items[].origin', prefix: 'ORIGIN_' },
 * // { expression: '*.items[].benchmarkPortfolio.origin', prefix: 'ORIGIN_' },
 * // ... 20+ more expressions
 *
 * // After (new simplified config):
 * if (enableAGraph) {
 *   const modified = removePrefixesFromAGraphEnums(response, [
 *     { fieldNames: ['operator'], prefix: 'FILTER_OPERATOR_', recursiveFieldPatterns: ['filters'] },
 *     { fieldNames: ['valueType'], prefix: 'FILTER_VALUE_TYPE_', recursiveFieldPatterns: ['filters'] }
 *   ]);
 * }
 * // Automatically handles all nesting levels!
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- any shape could use this
export function removePrefixesFromAGraphEnums<T extends Record<string, any>>(
  data: T,
  configs: EnumPrefixConfig[],
  options?: Pick<RecursiveTransformOptions, 'maxDepth'>
): T {
  if (!data) {
    return data;
  }

  const { maxDepth = DEFAULT_MAX_RECURSION_DEPTH } = options || {};

  return configs.reduce(
    (result, { fieldNames, prefix, recursiveFieldPatterns }) =>
      recursiveTransform(
        result,
        fieldNames,
        (value) => removePrefixFromAGraphEnum(value as string | undefined, prefix, true),
        {
          maxDepth,
          recursiveFieldPatterns,
        }
      ),
    data
  );
}

/**
 * Configuration for transforming responses based on URL patterns and HTTP methods.
 * This provides a scalable way to apply transformations to API responses.
 */
export interface ResponseTransformConfig {
  /**
   * URL pattern to match against response.config.url
   * Supports glob patterns (e.g., '/v1/recent-searches', '/v1/*', '**\/filters')
   */
  urlPattern: string;

  /**
   * HTTP method to match (optional, matches all methods if not specified)
   * e.g., 'GET', 'POST', 'PUT', 'DELETE'
   */
  method?: string;

  /**
   * Array of timestamp field names to transform (number \u2194 string).
   * Automatically handles nested structures (model, benchmark, portfolio, items).
   * e.g., ['asOf', 'creationTimestamp', 'lastUpdatedTimestamp']
   */
  timestampFields?: string[];

  /**
   * Array of enum prefix configurations to remove from response
   * e.g., [{ fieldNames: ['operator'], prefix: 'FILTER_OPERATOR_', recursiveFieldPatterns: ['filters'] }]
   */
  enumPrefixesToRemove?: EnumPrefixConfig[];

  /**
   * Collates openLots from positions[] and sets them on the portfolio's openLots field.
   * This is used to transform Portfolio (where openLots are on positions)
   * to the legacy Portfolio shape (where openLots are on the portfolio itself).
   *
   * - `true`: Treats data as a single portfolio (also processes nested benchmarkPortfolio/modelPortfolio)
   * - `{ recursiveFieldPatterns: string[] }`: Find portfolios in specified array fields recursively
   *   e.g., `{ recursiveFieldPatterns: ['items', 'portfolios'] }` finds portfolios in items[] and portfolios[] arrays
   *
   * @example
   * ```typescript
   * // Single portfolio at top level:
   * flattenOpenLots: true
   *
   * // Portfolios nested in arrays:
   * flattenOpenLots: { recursiveFieldPatterns: ['items', 'portfolios'] }
   * ```
   */
  flattenOpenLots?: boolean | { recursiveFieldPatterns?: string[]; maxDepth?: number };

  /**
   * Flattens portfolioEntityRefs array wrappers to match legacy response shape.
   * Replaces objects containing portfolioEntityRefs with just the array contents.
   *
   * - `string[]`: Array of field names to flatten
   *   e.g., ['resourceEntities']
   *
   * **Note**: Uses recursiveTransform with default recursiveFieldPatterns: ['*']
   * to automatically traverse all nested structures. If the field appears inside
   * arrays (e.g., items[].resourceEntities), it will be found and flattened.
   *
   * @example
   * ```typescript
   * // Flatten resourceEntities at top level and in all nested arrays
   * flattenPortfolioEntityRefs: ['resourceEntities']
   * ```
   */
  flattenPortfolioEntityRefs?: string[];

  /**
   * Flattens fieldValueMap objects into their parent objects to match legacy response shape.
   * Replaces objects containing fieldValueMap with a merged object where fieldValueMap contents
   * are spread into the parent.
   *
   * - `{ arrayFieldPath: string }`: Specifies the path to the array field containing objects with fieldValueMap
   *   Supports lodash path notation (e.g., 'accounts', 'faAccountsDataResponse.accounts', 'data.items')
   *
   * @example
   * ```typescript
   * // Input (AGraph):
   * {
   *   faAccountsDataResponse: {
   *     accounts: [
   *       {
   *         fieldNames: [...],
   *         fieldValueMap: { accountID: '123', marketValue: 1000 }
   *       }
   *     ]
   *   }
   * }
   *
   * // Output (Legacy):
   * {
   *   faAccountsDataResponse: {
   *     accounts: [
   *       {
   *         fieldNames: [...],
   *         accountID: '123',
   *         marketValue: 1000
   *       }
   *     ]
   *   }
   * }
   *
   * // Usage:
   * flattenFieldValueMaps: { arrayFieldPath: 'faAccountsDataResponse.accounts' }
   * flattenFieldValueMaps: { arrayFieldPath: 'accounts' } // for top-level
   * ```
   */
  flattenFieldValueMaps?: { arrayFieldPath: string };

  /**
   * Renames fields in the response to match legacy response shape.
   * Uses recursive traversal to automatically handle nested structures.
   *
   * Each entry maps a field `from` its AGraph name `to` the legacy name.
   * Optionally specify `recursiveFieldPatterns` to control which nested structures
   * are traversed (defaults to `['*']` to traverse all).
   *
   * @example
   * ```typescript
   * // Rename benchmarkPortfolio → benchmark at all nesting levels:
   * renameFields: [{ from: 'benchmarkPortfolio', to: 'benchmark' }]
   *
   * // Rename only within specific nested structures:
   * renameFields: [
   *   { from: 'benchmarkPortfolio', to: 'benchmark', recursiveFieldPatterns: ['results', 'items'] }
   * ]
   * ```
   */
  renameFields?: Array<{ from: string; to: string; recursiveFieldPatterns?: string[] }>;

  /**
   * Maximum recursion depth for transform functions.
   * Controls how many levels deep transformations will traverse in nested structures.
   *
   * @default DEFAULT_MAX_RECURSION_DEPTH (3, or AW_API_MAX_RECURSION_DEPTH env var)
   *
   * **Note**: maxDepth is now deprecated in ResponseTransformConfig.
   * All transforms use a fixed depth of 3, which matches actual Portfolio/Aggregate nesting depth:
   * - Portfolio → Model → Benchmark → BenchmarkRef (3 levels)
   *
   * **Why depth 3 is sufficient**:
   * - Portfolios render 1 benchmark level + reference
   * - Models render up to 2 nested levels + reference
   * - Maximum practical depth: portfolio → model → benchmark → benchmarkRef = 3
   *
   * @example
   * ```typescript
   * // All transforms now use depth 3 by default (maxDepth removed from configs):
   * { urlPattern: '/v1/portfolios/*', timestampFields: [...] }
   * { urlPattern: '/v1/filters', timestampFields: [...] }
   * ```
   */
  maxDepth?: number;
}

/**
 * Flatten openLots from positions into the portfolio-level openLots field.
 * This transforms the new AGraph API response shape (Portfolio)
 * to the legacy Portfolio shape where openLots are on the portfolio itself.
 *
 * @param data - The portfolio data object (or object containing portfolios)
 * @param options - How to locate portfolios:
 *   - `true`: Treats data as a single Portfolio (also processes nested benchmarkPortfolio/modelPortfolio)
 *   - `{ recursiveFieldPatterns: string[] }`: Find portfolios in specified array fields recursively
 * @returns The data with openLots flattened from positions to portfolio level
 *
 * @example
 * ```typescript
 * // Single portfolio:
 * flattenOpenLotsFromPositions(data, true);
 *
 * // Portfolios in items[] and portfolios[] arrays:
 * flattenOpenLotsFromPositions(data, { recursiveFieldPatterns: ['items', 'portfolios'] });
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- any shape could use this
export function flattenOpenLotsFromPositions<T extends Record<string, any>>(
  data: T,
  options?: boolean | { recursiveFieldPatterns?: string[]; maxDepth?: number }
): T {
  if (!data) {
    return data;
  }

  const clonedData = cloneDeep(data);

  /**
   * Process a single portfolio object to flatten its openLots.
   * Also recursively processes nested benchmarkPortfolio and modelPortfolio.
   */
  const processPortfolio = (portfolio: Portfolio): void => {
    if (!portfolio || typeof portfolio !== 'object') {
      return;
    }

    const positions = portfolio.positions;
    if (Array.isArray(positions)) {
      // Collate all openLots from all positions
      const allOpenLots: Position['openLots'] = [];
      positions.forEach((position: Position) => {
        if (Array.isArray(position?.openLots)) {
          allOpenLots.push(...position.openLots);
        }
      });

      /*
       * Set the flattened openLots on the portfolio
       * Using type assertion since we're adding openLots to match the legacy Portfolio shape
       */
      (portfolio as Portfolio & { openLots: typeof allOpenLots }).openLots = allOpenLots;
    }

    // Recursively process nested portfolios (these are part of Portfolio)
    if (portfolio.benchmarkPortfolio) {
      processPortfolio(portfolio.benchmarkPortfolio);
    }
    if (portfolio.modelPortfolio) {
      processPortfolio(portfolio.modelPortfolio);
    }
  };

  /**
   * Recursively search for portfolio objects in the data structure.
   * A portfolio is identified by having a positions array.
   */
  const findAndProcessPortfolios = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- handles any structure
    obj: any,
    fieldPatterns: string[],
    depth: number,
    maxDepth: number
  ): void => {
    if (!obj || typeof obj !== 'object' || depth > maxDepth) {
      return;
    }

    // Check if this object is a portfolio (has positions array)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- checking for positions
    if (Array.isArray(obj.positions)) {
      processPortfolio(obj as Portfolio);
    }

    // Recursively search in specified fields
    fieldPatterns.forEach((pattern) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- accessing field by name
      const value = obj[pattern];
      if (Array.isArray(value)) {
        value.forEach((item) => {
          findAndProcessPortfolios(item, fieldPatterns, depth + 1, maxDepth);
        });
      } else if (value && typeof value === 'object') {
        findAndProcessPortfolios(value, fieldPatterns, depth + 1, maxDepth);
      }
    });
  };

  if (options === true) {
    // Treat the data as a single Portfolio
    processPortfolio(clonedData as unknown as Portfolio);
  } else if (
    typeof options === 'object' &&
    options.recursiveFieldPatterns &&
    options.recursiveFieldPatterns.length > 0
  ) {
    // Find portfolios in specified array fields recursively
    const { recursiveFieldPatterns, maxDepth = DEFAULT_MAX_RECURSION_DEPTH } = options;
    findAndProcessPortfolios(clonedData, recursiveFieldPatterns, 0, maxDepth);
  }

  return clonedData;
}

/**
 * Distribute openLots from the portfolio level to each position based on posId.
 * This transforms the legacy Portfolio shape (where openLots are on the portfolio)
 * to the AGraph API request shape (Portfolio where openLots are on positions).
 *
 * Groups openLots by their `posId` and assigns each group to the corresponding position
 * where `position.id === openLot.posId`.
 *
 * @param data - The portfolio data object (or object containing portfolios)
 * @param options - How to locate portfolios:
 *   - `true`: Treats data as a single Portfolio
 *   - `{ recursiveFieldPatterns: string[] }`: Find portfolios in specified array fields recursively
 * @returns The data with openLots distributed from portfolio level to positions
 *
 * @example
 * ```typescript
 * // Input:
 * {
 *   openLots: [{ id: 'lot1', posId: '123' }, { id: 'lot2', posId: '123' }, { id: 'lot3', posId: '456' }],
 *   positions: [{ id: '123' }, { id: '456' }]
 * }
 * // Output:
 * {
 *   positions: [
 *     { id: '123', openLots: [{ id: 'lot1', posId: '123' }, { id: 'lot2', posId: '123' }] },
 *     { id: '456', openLots: [{ id: 'lot3', posId: '456' }] }
 *   ]
 * }
 *
 * // Portfolios in arrays:
 * distributeOpenLotsToPositions(data, { recursiveFieldPatterns: ['items'] });
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- any shape could use this
export function distributeOpenLotsToPositions<T extends Record<string, any>>(
  data: T,
  options?: boolean | { recursiveFieldPatterns?: string[]; maxDepth?: number }
): T {
  if (!data) {
    return data;
  }

  const clonedData = cloneDeep(data);

  /**
   * Process a single portfolio object to distribute its openLots to positions.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- portfolio shapes vary between legacy and AGraph
  const processPortfolio = (portfolio: any): void => {
    if (!portfolio || typeof portfolio !== 'object') {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- checking for openLots and positions arrays
    const openLots = portfolio.openLots;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- checking for positions array
    const positions = portfolio.positions;

    if (!Array.isArray(openLots) || !Array.isArray(positions)) {
      return;
    }

    // Group openLots by posId
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- openLot shapes vary
    const openLotsByPosId = new Map<string, any[]>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- openLot shapes vary
    openLots.forEach((openLot: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- accessing posId
      const posId = openLot?.posId as string | undefined;
      if (posId) {
        const existing = openLotsByPosId.get(posId) ?? [];
        existing.push(openLot);
        openLotsByPosId.set(posId, existing);
      }
    });

    // Assign openLots to each position
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- position shapes vary
    positions.forEach((position: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- accessing id
      const positionId = position?.id as string | undefined;
      if (positionId) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- setting openLots on position
        position.openLots = openLotsByPosId.get(positionId) ?? [];
      }
    });
  };

  /**
   * Recursively search for portfolio objects in the data structure.
   * A portfolio is identified by having both openLots and positions arrays.
   */
  const findAndProcessPortfolios = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- handles any structure
    obj: any,
    fieldPatterns: string[],
    depth: number,
    maxDepth: number
  ): void => {
    if (!obj || typeof obj !== 'object' || depth > maxDepth) {
      return;
    }

    // Check if this object is a portfolio (has openLots and positions arrays)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- checking for arrays
    if (Array.isArray(obj.openLots) && Array.isArray(obj.positions)) {
      processPortfolio(obj);
    }

    // Recursively search in specified fields
    fieldPatterns.forEach((pattern) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- accessing field by name
      const value = obj[pattern];
      if (Array.isArray(value)) {
        value.forEach((item) => {
          findAndProcessPortfolios(item, fieldPatterns, depth + 1, maxDepth);
        });
      } else if (value && typeof value === 'object') {
        findAndProcessPortfolios(value, fieldPatterns, depth + 1, maxDepth);
      }
    });
  };

  if (options === true) {
    // Treat the data as a single Portfolio
    processPortfolio(clonedData);
  } else if (
    typeof options === 'object' &&
    options.recursiveFieldPatterns &&
    options.recursiveFieldPatterns.length > 0
  ) {
    // Find portfolios in specified array fields recursively
    const { recursiveFieldPatterns, maxDepth = DEFAULT_MAX_RECURSION_DEPTH } = options;
    findAndProcessPortfolios(clonedData, recursiveFieldPatterns, 0, maxDepth);
  }

  return clonedData;
}

/**
 * Flatten portfolioEntityRefs array wrappers to match legacy response shape.
 * Transforms fields like { portfolioEntityRefs: [...] } to just [...]
 * Uses recursive traversal to handle nested structures automatically.
 *
 * @param data - The response data object to transform
 * @param fieldNames - Array of field names to flatten (e.g., ['resourceEntities'])
 * @param options - Optional recursion configuration (maxDepth, recursiveFieldPatterns)
 * @returns The data with portfolioEntityRefs wrappers flattened
 *
 * @example
 * ```typescript
 * // Input:
 * { resourceEntities: { portfolioEntityRefs: [{ id: '123', origin: 'ORIGIN_USER' }] } }
 * // After: flattenPortfolioEntityRefs(data, ['resourceEntities'])
 * // Output:
 * { resourceEntities: [{ id: '123', origin: 'ORIGIN_USER' }] }
 *
 * // With nested items:
 * const data = { items: [{ resourceEntities: { portfolioEntityRefs: [...] } }] };
 * flattenPortfolioEntityRefs(data, ['resourceEntities'], { recursiveFieldPatterns: ['items'] });
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- any shape could use this
export function flattenPortfolioEntityRefs<T extends Record<string, any>>(
  data: T,
  fieldNames: string[],
  options?: Pick<RecursiveTransformOptions, 'maxDepth' | 'recursiveFieldPatterns'>
): T {
  if (!data || !fieldNames || fieldNames.length === 0) {
    return data;
  }

  const { maxDepth = DEFAULT_MAX_RECURSION_DEPTH, recursiveFieldPatterns = ['*'] } = options || {};

  return recursiveTransform(
    data,
    fieldNames,
    (value) => {
      // If value is an object with portfolioEntityRefs array, unwrap it
      if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- checking for portfolioEntityRefs
        Array.isArray(value.portfolioEntityRefs)
      ) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return -- extracting portfolioEntityRefs array
        return value.portfolioEntityRefs;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- returns original value if not transformable
      return value;
    },
    { maxDepth, recursiveFieldPatterns }
  );
}

/**
 * Wrap portfolioEntityRefs arrays to match AGraph request shape.
 * Transforms array fields like [...] to { portfolioEntityRefs: [...] }
 * This is the reverse operation of flattenPortfolioEntityRefs.
 * Uses recursive traversal to handle nested structures automatically.
 *
 * @param data - The request data object to transform
 * @param fieldNames - Array of field names to wrap (e.g., ['resourceEntities'])
 * @param options - Optional recursion configuration (maxDepth, recursiveFieldPatterns)
 * @returns The data with arrays wrapped in portfolioEntityRefs objects
 *
 * @example
 * ```typescript
 * // Input:
 * { resourceEntities: [{ id: '123', origin: 'ORIGIN_USER' }] }
 * // After: wrapPortfolioEntityRefs(data, ['resourceEntities'])
 * // Output:
 * { resourceEntities: { portfolioEntityRefs: [{ id: '123', origin: 'ORIGIN_USER' }] } }
 *
 * // With nested items:
 * const data = { items: [{ resourceEntities: [...] }] };
 * wrapPortfolioEntityRefs(data, ['resourceEntities'], { recursiveFieldPatterns: ['items'] });
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- any shape could use this
export function wrapPortfolioEntityRefs<T extends Record<string, any>>(
  data: T,
  fieldNames: string[],
  options?: Pick<RecursiveTransformOptions, 'maxDepth' | 'recursiveFieldPatterns'>
): T {
  if (!data || !fieldNames || fieldNames.length === 0) {
    return data;
  }

  const { maxDepth = DEFAULT_MAX_RECURSION_DEPTH, recursiveFieldPatterns = ['*'] } = options || {};

  return recursiveTransform(
    data,
    fieldNames,
    (value) => {
      // If value is an array (not already wrapped), wrap it
      if (Array.isArray(value)) {
        return { portfolioEntityRefs: value };
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- returns original value if not an array
      return value;
    },
    { maxDepth, recursiveFieldPatterns }
  );
}

/**
 * Recursively rename fields in an object structure.
 * Traverses nested objects and arrays, renaming matching keys.
 *
 * @param data - The data object to transform
 * @param from - The field name to rename from
 * @param to - The field name to rename to
 * @param options - Recursive traversal options (maxDepth, recursiveFieldPatterns)
 * @returns New object with renamed fields (immutable - original object unchanged)
 *
 * @example
 * ```typescript
 * const data = { benchmarkPortfolio: { id: '123' }, model: { benchmarkPortfolio: { id: '456' } } };
 * const result = renameResponseFields(data, 'benchmarkPortfolio', 'benchmark',
 *   { recursiveFieldPatterns: ['*'] });
 * // Result: { benchmark: { id: '123' }, model: { benchmark: { id: '456' } } }
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- any shape could use this
export function renameResponseFields<T extends Record<string, any>>(
  data: T,
  from: string,
  to: string,
  options?: Pick<RecursiveTransformOptions, 'maxDepth' | 'recursiveFieldPatterns'>
): T {
  if (!data || !from || !to) {
    return data;
  }

  const { maxDepth = DEFAULT_MAX_RECURSION_DEPTH, recursiveFieldPatterns = ['*'] } = options || {};

  const clonedData = cloneDeep(data);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- recursive traversal requires any
  const traverse = (obj: any, depth: number): any => {
    if (!obj || typeof obj !== 'object' || depth > maxDepth) {
      return obj;
    }

    if (Array.isArray(obj)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- recursive traversal of arbitrary arrays
      return obj.map((item) => traverse(item, depth));
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- building arbitrary result object
    const result: Record<string, any> = {};

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- checked its an object
    Object.keys(obj).forEach((key) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- accessing dynamic field
      let value = obj[key];

      // Recurse into nested structures that match the patterns
      if (Array.isArray(value) || (isObject(value) && isRecursiveField(key, recursiveFieldPatterns))) {
        value = traverse(value, depth + 1);
      }

      // Rename the key if it matches
      const outputKey = key === from ? to : key;

      result[outputKey] = value;
    });

    return result;
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- traverse returns transformed object
  return traverse(clonedData, 0);
}

/**
 * Apply configured transformations to an Axios response.
 * Matches the response URL and method against the provided configurations
 * and applies the corresponding transformations (timestamp normalization, enum prefix removal).
 * Only runs transformations for the first matching config.
 *
 * @param response - The Axios response to transform
 * @param configs - Array of transformation configurations to apply
 * @returns The transformed response (mutates response.data in place)
 *
 * @example
 * ```typescript
 * const transformConfigs: ResponseTransformConfig[] = [
 *   {
 *     urlPattern: '/v1/recent-searches',
 *     method: 'GET',
 *     timestampFields: [
 *       'entities[*].userPreferenceMetadata.createdAt',
 *       'entities[*].userPreferenceMetadata.updatedAt'
 *     ]
 *   },
 *   {
 *     urlPattern: '/v1/filters/**',
 *     enumPrefixesToRemove: [
 *       { fieldNames: ['operator'], prefix: 'FILTER_OPERATOR_', recursiveFieldPatterns: ['filters'] }
 *     ]
 *   }
 * ];
 *
 * // In response interceptor:
 * applyResponseTransformations(response, transformConfigs);
 * ```
 */
export function applyAGraphResponseTransformations(
  response: AxiosResponse,
  configs: ResponseTransformConfig[]
): AxiosResponse {
  if (!response.data || !response.config.url) {
    return response;
  }

  const url = response.config.url.startsWith('/') ? response.config.url : `/${response.config.url}`;
  const urlPath = url.split('?')[0];
  const method = response.config.method?.toUpperCase();

  for (const config of configs) {
    // Check if URL matches the pattern
    const urlMatches = urlPath ? minimatch(urlPath, config.urlPattern) : false;

    // Check if method matches (if specified)
    const methodMatches = !config.method || config.method.toUpperCase() === method;

    if (urlMatches && methodMatches) {
      // Get maxDepth from config or use default
      const maxDepth = config.maxDepth ?? DEFAULT_MAX_RECURSION_DEPTH;

      // Apply timestamp normalization
      if (config.timestampFields && config.timestampFields.length > 0) {
        response.data = transformTimestamps(response.data, config.timestampFields, 'normalize', {
          maxDepth,
          recursiveFieldPatterns: ['*'], // Wildcard: traverse all nested objects
        });
      }

      // Apply enum prefix removal
      if (config.enumPrefixesToRemove && config.enumPrefixesToRemove.length > 0) {
        response.data = removePrefixesFromAGraphEnums(response.data, config.enumPrefixesToRemove, { maxDepth });
      }

      // Flatten openLots from positions to portfolio level
      if (config.flattenOpenLots) {
        response.data = flattenOpenLotsFromPositions(response.data, config.flattenOpenLots);
      }

      // Flatten portfolioEntityRefs wrappers
      if (config.flattenPortfolioEntityRefs && config.flattenPortfolioEntityRefs.length > 0) {
        response.data = flattenPortfolioEntityRefs(response.data, config.flattenPortfolioEntityRefs);
      }

      // Flatten fieldValueMap objects
      if (config.flattenFieldValueMaps) {
        response.data = flattenFieldValueMaps(response.data, config.flattenFieldValueMaps.arrayFieldPath);
      }

      // Rename fields
      if (config.renameFields && config.renameFields.length > 0) {
        for (const { from, to, recursiveFieldPatterns } of config.renameFields) {
          response.data = renameResponseFields(response.data, from, to, {
            maxDepth,
            recursiveFieldPatterns: recursiveFieldPatterns ?? ['*'],
          });
        }
      }

      break;
    }
  }

  return response;
}

/**
 * Flatten fieldValueMap objects into their parent objects.
 * This transforms the AGraph API response shape (where data is nested in fieldValueMap)
 * to the legacy shape (where data is on the parent object).
 *
 * @param data - The response data object
 * @param arrayFieldPath - The path to the array field containing objects with fieldValueMap (supports lodash path notation)
 * @returns The data with fieldValueMap contents merged into parent objects
 *
 * @example
 * ```typescript
 * // Input:
 * {
 *   faAccountsDataResponse: {
 *     accounts: [
 *       {
 *         fieldNames: ['id', 'value'],
 *         fieldValueMap: { id: '123', value: 1000 }
 *       }
 *     ]
 *   }
 * }
 * // Output:
 * {
 *   faAccountsDataResponse: {
 *     accounts: [
 *       {
 *         fieldNames: ['id', 'value'],
 *         id: '123',
 *         value: 1000
 *       }
 *     ]
 *   }
 * }
 *
 * // Usage:
 * flattenFieldValueMaps(data, 'faAccountsDataResponse.accounts')
 * flattenFieldValueMaps(data, 'accounts') // top-level array
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- handles any response shape
export function flattenFieldValueMaps<T extends Record<string, any>>(data: T, arrayFieldPath: string): T {
  if (!data || !arrayFieldPath) {
    return data;
  }

  const clonedData = cloneDeep(data);

  // Use lodash get to support nested paths
  const array = get(clonedData, arrayFieldPath);
  if (!Array.isArray(array)) {
    return clonedData;
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-explicit-any -- handles any response shape
  const transformedArray = array.map((item: any) => {
    if (!item || typeof item !== 'object') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- handles any response shape
      return item;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- checking for fieldValueMap
    const fieldValueMap = item.fieldValueMap;
    if (!fieldValueMap || typeof fieldValueMap !== 'object') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- handles any response shape
      return item;
    }

    // Merge fieldValueMap into parent and remove the fieldValueMap property
    const { fieldValueMap: _, ...rest } = item;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- returning merged object
    return { ...rest, ...fieldValueMap };
  });

  // Use lodash set to update the nested path
  // eslint-disable-next-line @blk/aladdin-wealth/prefer-immer -- using cloneDeep for compatibilty
  set(clonedData, arrayFieldPath, transformedArray);

  return clonedData;
}

/**
 * Configuration for excluding query parameters from specific API endpoints
 */
export interface ParamExclusionRule {
  /** URL prefix or prefixes to match against (checks if config.url starts with any of these values) */
  urlPrefix: string | string[];
  /** Parameter names to exclude for URLs starting with these prefixes */
  excludedParams: string[];
}

/**
 * Determines if a parameter should be excluded based on URL and exclusion rules.
 * Always returns false if not on cloud env - i.e. we only ever exclude params if we're on the cloud.
 * @param url - The API endpoint URL
 * @param paramName - The parameter name to check
 * @param exclusionRules - Array of rules defining which params to exclude for which URLs
 * @returns true if the parameter should be excluded
 */
export function shouldExcludeParam(
  url: string | undefined,
  paramName: string,
  exclusionRules: ParamExclusionRule[]
): boolean {
  if (!url || !isCloud()) {
    return false;
  }

  return exclusionRules.some((rule) => {
    const prefixes = Array.isArray(rule.urlPrefix) ? rule.urlPrefix : [rule.urlPrefix];
    const urlMatches = prefixes.some((prefix) => url.startsWith(prefix));
    const paramMatches = rule.excludedParams.includes(paramName);
    return urlMatches && paramMatches;
  });
}

/**
 * Returns the parameter value or undefined if it should be excluded
 * @param url - The API endpoint URL
 * @param paramName - The parameter name
 * @param value - The parameter value
 * @param exclusionRules - Array of rules defining which params to exclude for which URLs
 * @returns The value if not excluded, undefined otherwise
 */
export function conditionalParam<T>(
  url: string | undefined,
  paramName: string,
  value: T,
  exclusionRules: ParamExclusionRule[]
): T | undefined {
  return shouldExcludeParam(url, paramName, exclusionRules) ? undefined : value;
}

/**
 * Remove field(s) from an object if their value is an empty string.
 * Supports nested field access using dot notation (e.g., 'foo.bar.baz').
 * Returns a new object without mutating the original.
 *
 * @param data - The object to process
 * @param fieldPaths - Single field path or array of field paths (using dot notation like 'foo.bar.baz' or simple 'fieldName')
 * @returns A new object with the field(s) removed if they were empty strings
 *
 * @example
 * ```typescript
 * const payload = { name: 'test', expiryTimestamp: '', other: 'value' };
 * const cleaned = removeFieldIfEmptyString(payload, 'expiryTimestamp');
 * // Result: { name: 'test', other: 'value' }
 * ```
 * @example
 * ```typescript
 * const payload = { foo: { bar: { baz: '' } }, other: 'value' };
 * const cleaned = removeFieldIfEmptyString(payload, 'foo.bar.baz');
 * // Result: { foo: { bar: {} }, other: 'value' }
 * ```
 * @example
 * ```typescript
 * const payload = { field1: '', field2: '', field3: 'value' };
 * const cleaned = removeFieldIfEmptyString(payload, ['field1', 'field2']);
 * // Result: { field3: 'value' }
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- any shape could use this
export function removeFieldIfEmptyString<T extends Record<string, any>>(data: T, fieldPaths: string | string[]): T {
  if (!data) {
    return data;
  }

  // Clone to avoid mutating the original
  const clonedData = cloneDeep(data);

  // Convert to array for uniform processing
  const paths = Array.isArray(fieldPaths) ? fieldPaths : [fieldPaths];

  // Process each field path
  paths.forEach((fieldPath) => {
    // Use lodash get to find the value at the specified path (preserves empty strings)
    const value = get(clonedData, fieldPath);

    // If the value is an empty string, remove it using lodash unset
    if (value === '') {
      unset(clonedData, fieldPath);
    }
  });

  return clonedData;
}

/**
 * Recursively remove null-valued properties from a request payload for AGraph compatibility.
 *
 * AGraph endpoints reject payloads with null-valued properties. This function creates a new
 * object with all null-valued properties removed at any nesting level. Immutability is
 * guaranteed - the original object is never modified.
 *
 * **Behavior**:
 * - Removes properties with `null` values from objects
 * - Preserves properties with `undefined` values
 * - Preserves `null` values inside arrays (only removes null properties from objects)
 * - Handles deeply nested structures automatically
 * - Empty objects `{}` and empty arrays `[]` are preserved
 *
 * @param data - The data payload to modify (arbitrary object structure)
 * @param fieldPath - Optional field path to target a specific part of the payload
 *                    (e.g., 'filters' to target the filters array, 'data.filters' for nested path).
 *                    Supports dot notation for nested fields. If not provided,
 *                    null values are removed from the entire payload.
 * @returns A new object with null-valued properties removed (original unchanged)
 *
 * @example
 * ```typescript
 * const payload = {
 *   pkg: 'ARM_DEMO_B',
 *   filters: [{ colTag: 'lastStatus', compareColTag: null, end: null, operator: 'IN', value: null }]
 * };
 * if (isAGraphEnabled()) {
 *   const modified = normalizeNullValuesForAGraph(payload, 'filters');
 * }
 * // Result: { pkg: 'ARM_DEMO_B', filters: [{ colTag: 'lastStatus', operator: 'IN' }] }
 * ```
 *
 * @example
 * ```typescript
 * // Without field path - removes nulls from entire payload
 * const payload = { name: 'test', value: null, nested: { a: 1, b: null } };
 * const modified = normalizeNullValuesForAGraph(payload);
 * // Result: { name: 'test', nested: { a: 1 } }
 * ```
 *
 * @example
 * ```typescript
 * // Nested field path with dot notation
 * const payload = { data: { filters: [{ a: null, b: 'test' }] } };
 * const result = normalizeNullValuesForAGraph(payload, 'data.filters');
 * // Result: { data: { filters: [{ b: 'test' }] } }
 * ```
 *
 * @example
 * ```typescript
 * // Preserves undefined, only removes null
 * const payload = { a: undefined, b: null, c: 'test' };
 * const result = normalizeNullValuesForAGraph(payload);
 * // Result: { a: undefined, c: 'test' }
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- any shape could use this
export function normalizeNullValuesForAGraph<T extends Record<string, any>>(data: T, fieldPath?: string): T {
  if (!data) {
    return data;
  }

  /**
   * Recursively remove null-valued properties from objects.
   * Uses functional reduce pattern for cleaner, more maintainable code.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- recursive traversal requires any
  const removeNulls = (obj: any): any => {
    // Early return for null/undefined primitives
    if (obj === null || obj === undefined) {
      return obj;
    }

    // Arrays - recursively transform each item
    if (Array.isArray(obj)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- recursive call returns any
      return obj.map((item) => removeNulls(item));
    }

    // Objects - use reduce to build new object without null-valued properties
    if (typeof obj === 'object') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- building arbitrary object structure
      return Object.keys(obj as Record<string, any>).reduce<Record<string, any>>((acc, key) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- obj is checked to be an object
        const value = obj[key];
        // Only add property if value is not null (undefined is preserved)
        if (value !== null) {
          acc[key] = removeNulls(value);
        }
        return acc;
      }, {});
    }

    // Primitives - return as-is
    return obj;
  };

  // Handle scoped transformation with field path
  if (fieldPath) {
    const clonedData = cloneDeep(data);
    const target = get(clonedData, fieldPath);

    if (target !== null && target !== undefined) {
      const transformed = removeNulls(target);
      // eslint-disable-next-line @blk/aladdin-wealth/prefer-immer -- we're returning a new object
      set(clonedData, fieldPath, transformed);
    }

    return clonedData;
  }

  // No scoping - transform entire payload
  return removeNulls(cloneDeep(data)) as T;
}

/**
 * Call-site helper for generated AGraph SDK functions.
 *
 * Two conveniences over calling the SDK function directly:
 *   1. Relaxes the `headers` field of the options to optional. The SDK marks
 *      `VND.com.blackrock.*` fields as required, but the per-API request
 *      interceptor overwrites them via `setAGraphHeaders`, so requiring
 *      callers to supply placeholder strings is noise.
 *   2. Defaults `throwOnError` to `true` so success paths return a classic
 *      `AxiosResponse` and HTTP errors throw. Callers may override by
 *      passing `throwOnError: false` explicitly.
 *
 * Usage:
 *   const resp = await aGraph(localizationApiListLabels, {
 *     query: { locale: 'en' },
 *   });
 *   const { localizations } = resp.data;
 */
/*
 * Narrow an SDK function's return to the `throwOnError: true` success branch.
 * `ReturnType` on a generic SDK function may keep `ThrowOnError` at its
 * constraint (`boolean`), expanding to the throwing | non-throwing union.
 * `Awaited` flattens the Promise(s), then `Extract<..., { error: undefined }>`
 * picks the success variant where `data` is non-optional.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- structural relaxation for generic SDK functions
type AgraphResult<TFn extends (opts: any) => any> = Promise<Extract<Awaited<ReturnType<TFn>>, { error: undefined }>>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- structural relaxation, any is load-bearing
export function aGraph<TFn extends (opts: any) => any>(
  fn: TFn,
  opts: Omit<Parameters<TFn>[0], 'headers' | 'throwOnError'> & {
    headers?: Partial<Parameters<TFn>[0] extends { headers: infer H } ? H : never>;
    throwOnError?: boolean;
  }
): AgraphResult<TFn> {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- return type is constrained by AgraphResult
  return fn({ throwOnError: true, ...opts });
}
